import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { ArrowLeft, Plus, Trash, Pencil } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { ModuleGuide, EmptyState } from '../components/GuidanceComponents';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LABOUR_SOURCES = ['in_house', 'subcontractor', 'temporary'];
const STATUS_OPTIONS = ['Planned', 'In Progress', 'Completed'];

const STATUS_COLORS = {
  Planned: 'bg-zinc-100 text-zinc-700',
  'In Progress': 'bg-amber-50 text-amber-700',
  Completed: 'bg-green-50 text-green-700',
};

export const ManpowerPlanning = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [plans, setPlans] = useState([]);
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({
    trade_id: '', num_workers: 1, duration_days: 1, cost_per_worker_per_day: 0,
    start_date: '', end_date: '', notes: '',
    skilled_count: 0, unskilled_count: 0, supervisor_count: 0,
    labour_source: 'in_house', actual_workers: 0, actual_cost: 0, status: 'Planned',
  });

  useEffect(() => { fetchData(); }, [projectId]);

  const fetchData = async () => {
    try {
      const [plansRes, tradesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/projects/${projectId}/manpower`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/trades`, { withCredentials: true }),
      ]);
      setPlans(plansRes.data);
      setTrades(tradesRes.data);
      if (tradesRes.data.length > 0 && !form.trade_id) setForm((f) => ({ ...f, trade_id: tradesRes.data[0].id }));
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  const resetForm = () => {
    setForm({
      trade_id: trades[0]?.id || '', num_workers: 1, duration_days: 1, cost_per_worker_per_day: 0,
      start_date: '', end_date: '', notes: '',
      skilled_count: 0, unskilled_count: 0, supervisor_count: 0,
      labour_source: 'in_house', actual_workers: 0, actual_cost: 0, status: 'Planned',
    });
    setEditId(null);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (editId) {
        await axios.put(`${BACKEND_URL}/api/manpower/${editId}`, form, { withCredentials: true });
        toast.success('Plan updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/projects/${projectId}/manpower`, form, { withCredentials: true });
        toast.success('Plan created');
      }
      setDialogOpen(false); resetForm(); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
  };

  const handleEdit = (plan) => {
    setForm({
      trade_id: plan.trade_id, num_workers: plan.num_workers, duration_days: plan.duration_days,
      cost_per_worker_per_day: plan.cost_per_worker_per_day, start_date: plan.start_date || '', end_date: plan.end_date || '',
      notes: plan.notes, skilled_count: plan.skilled_count || 0, unskilled_count: plan.unskilled_count || 0,
      supervisor_count: plan.supervisor_count || 0, labour_source: plan.labour_source || 'in_house',
      actual_workers: plan.actual_workers || 0, actual_cost: plan.actual_cost || 0, status: plan.status || 'Planned',
    });
    setEditId(plan.id);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this manpower plan?')) return;
    try { await axios.delete(`${BACKEND_URL}/api/manpower/${id}`, { withCredentials: true }); toast.success('Deleted'); fetchData(); }
    catch { toast.error('Failed to delete'); }
  };

  const totalPlannedCost = plans.reduce((s, p) => s + p.total_cost, 0);
  const totalActualCost = plans.reduce((s, p) => s + (p.actual_cost || 0), 0);
  const totalPlannedWorkers = plans.reduce((s, p) => s + p.num_workers, 0);
  const totalActualWorkers = plans.reduce((s, p) => s + (p.actual_workers || 0), 0);
  const costVariance = totalPlannedCost - totalActualCost;

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-pulse text-zinc-600">Loading...</div></div>;

  return (
    <div>
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button onClick={() => navigate(`/projects/${projectId}?tab=control`)} variant="outline" className="border-zinc-300 rounded-none hover:bg-zinc-100 mb-3" data-testid="back-button">
            <ArrowLeft size={18} className="mr-1.5" /> Back to Control Tower
          </Button>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-900">Manpower Planning</h1>
        </div>
      </header>
      <main className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <ModuleGuide text="Plan and track worker allocation by trade. Assign skilled, unskilled, and supervisory manpower for each execution phase." />

        {/* Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
          <div className="bg-white border border-zinc-200 p-4">
            <span className="text-xs font-bold uppercase text-zinc-500">Planned Workers</span>
            <p className="text-2xl font-bold text-zinc-900 mt-1">{totalPlannedWorkers}</p>
          </div>
          <div className="bg-white border border-zinc-200 p-4">
            <span className="text-xs font-bold uppercase text-zinc-500">Actual Workers</span>
            <p className="text-2xl font-bold text-zinc-900 mt-1">{totalActualWorkers}</p>
          </div>
          <div className="bg-white border border-zinc-200 p-4">
            <span className="text-xs font-bold uppercase text-zinc-500">Planned Cost</span>
            <p className="text-2xl font-bold text-blue-700 mt-1">₹{totalPlannedCost.toLocaleString('en-IN')}</p>
          </div>
          <div className="bg-white border border-zinc-200 p-4">
            <span className="text-xs font-bold uppercase text-zinc-500">Actual Cost</span>
            <p className="text-2xl font-bold text-zinc-900 mt-1">₹{totalActualCost.toLocaleString('en-IN')}</p>
          </div>
          <div className="bg-white border border-zinc-200 p-4">
            <span className="text-xs font-bold uppercase text-zinc-500">Cost Variance</span>
            <p className={`text-2xl font-bold mt-1 ${costVariance >= 0 ? 'text-green-700' : 'text-red-700'}`}>
              {costVariance >= 0 ? '+' : ''}₹{costVariance.toLocaleString('en-IN')}
            </p>
          </div>
          <div className="bg-white border border-zinc-200 p-4">
            <span className="text-xs font-bold uppercase text-zinc-500">Plans</span>
            <p className="text-2xl font-bold text-zinc-900 mt-1">{plans.length}</p>
          </div>
        </div>

        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-zinc-800">Manpower Plans</h2>
          <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-blue-700 hover:bg-blue-800 text-white rounded-none" data-testid="add-manpower-button">
                <Plus size={18} className="mr-1.5" /> Add Plan
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border border-zinc-200 rounded-none shadow-xl max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="text-xl font-bold">{editId ? 'Edit' : 'New'} Manpower Plan</DialogTitle></DialogHeader>
              <form onSubmit={handleSave} className="space-y-4 mt-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs font-bold uppercase text-zinc-500">Trade *</Label>
                    <select value={form.trade_id} onChange={(e) => setForm({ ...form, trade_id: e.target.value })}
                      className="mt-1 w-full bg-zinc-50 border border-zinc-200 rounded-none p-2.5 text-sm" data-testid="mp-trade-select">
                      {trades.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase text-zinc-500">Labour Source</Label>
                    <select value={form.labour_source} onChange={(e) => setForm({ ...form, labour_source: e.target.value })}
                      className="mt-1 w-full bg-zinc-50 border border-zinc-200 rounded-none p-2.5 text-sm" data-testid="mp-source">
                      {LABOUR_SOURCES.map(s => <option key={s} value={s}>{s.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>)}
                    </select>
                  </div>
                </div>

                <fieldset className="border border-zinc-200 p-3">
                  <legend className="text-xs font-bold uppercase text-zinc-500 px-2">Planned Manpower</legend>
                  <div className="grid grid-cols-3 gap-3">
                    <div><Label className="text-xs text-zinc-500">Total Workers *</Label>
                      <Input type="number" min="1" value={form.num_workers} onChange={(e) => setForm({ ...form, num_workers: parseInt(e.target.value) || 1 })} className="mt-1 rounded-none" data-testid="mp-workers" /></div>
                    <div><Label className="text-xs text-zinc-500">Days *</Label>
                      <Input type="number" min="1" value={form.duration_days} onChange={(e) => setForm({ ...form, duration_days: parseInt(e.target.value) || 1 })} className="mt-1 rounded-none" data-testid="mp-days" /></div>
                    <div><Label className="text-xs text-zinc-500">Cost/Worker/Day *</Label>
                      <Input type="number" min="0" value={form.cost_per_worker_per_day} onChange={(e) => setForm({ ...form, cost_per_worker_per_day: parseFloat(e.target.value) || 0 })} className="mt-1 rounded-none" data-testid="mp-cost" /></div>
                  </div>
                  <div className="grid grid-cols-3 gap-3 mt-3">
                    <div><Label className="text-xs text-zinc-500">Skilled</Label>
                      <Input type="number" min="0" value={form.skilled_count} onChange={(e) => setForm({ ...form, skilled_count: parseInt(e.target.value) || 0 })} className="mt-1 rounded-none" data-testid="mp-skilled" /></div>
                    <div><Label className="text-xs text-zinc-500">Unskilled</Label>
                      <Input type="number" min="0" value={form.unskilled_count} onChange={(e) => setForm({ ...form, unskilled_count: parseInt(e.target.value) || 0 })} className="mt-1 rounded-none" data-testid="mp-unskilled" /></div>
                    <div><Label className="text-xs text-zinc-500">Supervisor</Label>
                      <Input type="number" min="0" value={form.supervisor_count} onChange={(e) => setForm({ ...form, supervisor_count: parseInt(e.target.value) || 0 })} className="mt-1 rounded-none" data-testid="mp-supervisor" /></div>
                  </div>
                </fieldset>

                <fieldset className="border border-zinc-200 p-3">
                  <legend className="text-xs font-bold uppercase text-zinc-500 px-2">Actual Tracking</legend>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs text-zinc-500">Actual Workers</Label>
                      <Input type="number" min="0" value={form.actual_workers} onChange={(e) => setForm({ ...form, actual_workers: parseInt(e.target.value) || 0 })} className="mt-1 rounded-none" data-testid="mp-actual-workers" /></div>
                    <div><Label className="text-xs text-zinc-500">Actual Cost (₹)</Label>
                      <Input type="number" min="0" value={form.actual_cost} onChange={(e) => setForm({ ...form, actual_cost: parseFloat(e.target.value) || 0 })} className="mt-1 rounded-none" data-testid="mp-actual-cost" /></div>
                  </div>
                </fieldset>

                <div className="grid grid-cols-3 gap-3">
                  <div><Label className="text-xs font-bold uppercase text-zinc-500">Start Date</Label>
                    <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1 rounded-none" data-testid="mp-start" /></div>
                  <div><Label className="text-xs font-bold uppercase text-zinc-500">End Date</Label>
                    <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="mt-1 rounded-none" data-testid="mp-end" /></div>
                  <div><Label className="text-xs font-bold uppercase text-zinc-500">Status</Label>
                    <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                      className="mt-1 w-full bg-zinc-50 border border-zinc-200 rounded-none p-2.5 text-sm" data-testid="mp-status">
                      {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
                <div><Label className="text-xs font-bold uppercase text-zinc-500">Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 rounded-none" data-testid="mp-notes" /></div>
                <p className="text-sm text-zinc-500 bg-zinc-50 p-2 border">Planned Cost: ₹{(form.num_workers * form.duration_days * form.cost_per_worker_per_day).toLocaleString('en-IN')}</p>
                <Button type="submit" className="w-full bg-blue-700 hover:bg-blue-800 text-white rounded-none py-5" data-testid="mp-save">{editId ? 'Update' : 'Create'} Plan</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {plans.length === 0 ? (
          <div className="bg-white border border-zinc-200 p-12 text-center text-zinc-500">No manpower plans yet</div>
        ) : (
          <div className="bg-white border border-zinc-200 overflow-x-auto">
            <table className="w-full text-sm" data-testid="manpower-table">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr>
                  {['Trade', 'Source', 'Planned', 'S/U/Sv', 'Days', '₹/Day', 'Planned Cost', 'Actual Workers', 'Actual Cost', 'Variance', 'Status', 'Dates', ''].map((h) => (
                    <th key={h} className="px-3 py-2.5 text-left text-xs font-bold uppercase tracking-wider text-zinc-500 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {plans.map((p) => {
                  const variance = p.total_cost - (p.actual_cost || 0);
                  return (
                    <tr key={p.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`mp-row-${p.id}`}>
                      <td className="px-3 py-3 font-medium">{p.trade_name}</td>
                      <td className="px-3 py-3 text-xs">{(p.labour_source || 'in_house').replace('_', ' ')}</td>
                      <td className="px-3 py-3">{p.num_workers}</td>
                      <td className="px-3 py-3 text-xs">{p.skilled_count || 0}/{p.unskilled_count || 0}/{p.supervisor_count || 0}</td>
                      <td className="px-3 py-3">{p.duration_days}</td>
                      <td className="px-3 py-3">₹{p.cost_per_worker_per_day.toLocaleString('en-IN')}</td>
                      <td className="px-3 py-3 font-bold text-blue-700">₹{p.total_cost.toLocaleString('en-IN')}</td>
                      <td className="px-3 py-3">{p.actual_workers || 0}</td>
                      <td className="px-3 py-3">₹{(p.actual_cost || 0).toLocaleString('en-IN')}</td>
                      <td className={`px-3 py-3 font-medium ${variance >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                        {variance >= 0 ? '+' : ''}₹{variance.toLocaleString('en-IN')}
                      </td>
                      <td className="px-3 py-3"><span className={`text-xs px-2 py-0.5 ${STATUS_COLORS[p.status || 'Planned']}`}>{p.status || 'Planned'}</span></td>
                      <td className="px-3 py-3 text-xs whitespace-nowrap">{p.start_date || '—'}{p.end_date ? ` → ${p.end_date}` : ''}</td>
                      <td className="px-3 py-3">
                        <div className="flex gap-1">
                          <button onClick={() => handleEdit(p)} className="p-1 text-zinc-500 hover:text-blue-700" data-testid={`mp-edit-${p.id}`}><Pencil size={14} /></button>
                          <button onClick={() => handleDelete(p.id)} className="p-1 text-zinc-500 hover:text-red-700" data-testid={`mp-del-${p.id}`}><Trash size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
};
